// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { useEffect, useRef } from 'react'; // eslint-disable-line no-unused-vars

import Dropdown from '../Dropdown';
import { jsx } from '@emotion/core';

import Loader from './Loader';
import MenuItem from './MenuItem';
import { menuCss, menuItemTextCss } from './Styles';

import type { ComponentType } from 'react';
import type {
  GetItemPropsReturn,
  getMenuPropsReturn as GetMenuPropsReturn,
} from 'downshift';

import type { Item } from './MenuItem';

/** @jsx jsx */

// TODO(vitorbal):
// Eventually we could pass more props here as needed, to allow more powerful item customizations.
// E.g.: `isSelected`, `isHighlighted`, etc.
type MenuItemComponentProps = {
  item: Item,
  getLabelForItem: (item: Item) => string,
};
const DefaultMenuItemComponent = (props: MenuItemComponentProps) => (
  <div css={menuItemTextCss}>{props.getLabelForItem(props.item)}</div>
);

export type Props = {
  /** The list of items. Each item gets passed as an `item` prop to `MenuItemComponent` to be rendered. */
  items: Array<Item>,
  /** Whether the menu is currently open or not. */
  isOpen: boolean,
  /** The index that should be highlighted. */
  highlightedIndex?: number,
  /**
   * a11y label that describes the options available inside this menu, e.g. "List of items", "List of Countries", etc.
   */
  ariaLabel: string,
  /**
   * Callback that should be passed to the menu root element. Should be provided by the `Downshift` instance that wraps this component.
   */
  getMenuProps: (options: { 'aria-label': string }) => GetMenuPropsReturn,
  /**
   * Callback that should be passed to each item. Should be provided by the `Downshift` instance that wraps this component.
   */
  getItemProps: (options: { item: Item }) => GetItemPropsReturn,
  /**
   * The Component to use for rendering each item in the list of `items`.
   */
  MenuItemComponent: ComponentType<*>,
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `key` property on each item object.
   */
  getKeyForItem: (item: Item) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item) => string,
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean,
  /** Show loading indicator */
  isLoading?: boolean,
};

const Menu = (props: Props) => {
  const {
    ariaLabel,
    getItemProps,
    getLabelForItem,
    getKeyForItem,
    getMenuProps,
    highlightedIndex,
    isItemDisabled,
    isOpen,
    items,
    MenuItemComponent,
  } = props;

  const highlightedItemRef = useRef();
  useEffect(() => {
    if (isOpen && highlightedItemRef.current) {
      // Focus highlighted item when menu is displayed on screen.
      // This ensures users using assistive technologies are able to navigate
      // menu items immediately.
      highlightedItemRef.current.focus();
    }
  }, [isOpen]);

  return (
    // Note: we keep this div outside of the `isOpen` conditional for accessibility reasons
    <div
      css={menuCss}
      tabIndex={-1}
      {...getMenuProps({ 'aria-label': ariaLabel })}
    >
      {isOpen && (
        <Dropdown horizontal="right" isBlock={true} tag="ul" vertical="below">
          {items.map((item: Item, index: number) => (
            <MenuItem
              getLabelForItem={getLabelForItem}
              index={index}
              isDisabled={isItemDisabled(item)}
              isHighlighted={index === highlightedIndex}
              item={item}
              key={getKeyForItem(item)}
              liProps={getItemProps({
                index,
                item,
                disabled: isItemDisabled(item),
              })}
              MenuItemComponent={MenuItemComponent}
            />
          ))}
          {props.isLoading && (
            <MenuItem
              getLabelForItem={getLabelForItem}
              index={items.length}
              isDisabled={true}
              item="Loading"
              key="__loading"
              liProps={getItemProps({
                index: items.length,
                item: 'Loading',
                disabled: true,
              })}
              MenuItemComponent={Loader}
            />
          )}
        </Dropdown>
      )}
    </div>
  );
};

Menu.defaultProps = {
  MenuItemComponent: DefaultMenuItemComponent,
  getKeyForItem: (item: Item) => item.value,
  getLabelForItem: (item: Item) => item.label,
  highlightedIndex: -1,
  isItemDisabled: (item: Item) => item.isDisabled,
};

export default Menu;
